import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Laptop, Smartphone, Tablet, Gamepad2, WashingMachine, HardDrive, Phone, Mail, MapPin, Clock, Star, CheckCircle2, Send, Zap, ArrowRight, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { mockServices, mockPrices, mockTestimonials, mockGallery, mockFAQ } from '../mock';
import { trackLead } from '../components/FacebookPixel';
import ThemeToggle from '../components/ThemeToggle';
import HeroCarousel from '../components/HeroCarousel';
import SocialLinks from '../components/SocialLinks';
import Footer from '../components/Footer';
import HeaderSocials from '../components/HeaderSocials';

const iconMap = {
  Laptop,
  Smartphone,
  Tablet,
  Gamepad2,
  WashingMachine,
  HardDrive
};

const HomePage = () => {
  const [bookingForm, setBookingForm] = useState({
    name: '',
    phone: '',
    device: '',
    issue: ''
  });

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  const [gallery, setGallery] = useState(mockGallery);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Load gallery from API
  useEffect(() => {
    const loadGallery = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setGallery(data);
          }
        }
      } catch (error) {
        console.error('Error loading gallery:', error);
      }
    };
    loadGallery();
  }, []);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bookingForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Ваш запит прийнято! Ми зв\'яжемося з вами найближчим часом.');
        trackLead({ content_name: 'booking', content_category: bookingForm.device || 'repair' });
        setBookingForm({ name: '', phone: '', device: '', issue: '' });
      } else {
        toast.error(data.detail || 'Помилка відправки заявки. Спробуйте ще раз.');
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error('Помилка відправки заявки. Перевірте з\'єднання та спробуйте ще раз.');
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contactForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Дякуємо за звернення! Ми відповімо вам якнайшвидше.');
        trackLead({ content_name: 'contact' });
        setContactForm({ name: '', email: '', message: '' });
      } else {
        toast.error(data.detail || 'Помилка відправки повідомлення. Спробуйте ще раз.');
      }
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast.error('Помилка відправки повідомлення. Перевірте з\'єднання та спробуйте ще раз.');
    }
  };

  const scrollToBooking = () => {
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="homepage">
      <Helmet>
        <title>CIMM Computers - Ремонт ноутбуків та телефонів в Одесі | Безкоштовна діагностика</title>
        <meta name="description" content="Професійний ремонт ноутбуків, телефонів, планшетів в Одесі. Чистка ноутбуків, прискорення ноутбука, збірка комп'ютерів, обслуговування консолей, ремонт зарядних станцій. Безкоштовна діагностика. ☎ +38 097 417 8788" />
        <meta name="keywords" content="ремонт ноутбуків Одеса, ремонт телефонів Одеса, чистка ноутбуків Одеса, прискорення ноутбука Одеса, збірка комп'ютерів Одеса, обслуговування ігрових приставок і консолей Одеса, ремонт зарядних станцій Одеса, ремонт iphone, ремонт macbook, ремонт samsung, cimm одеса" />
        <meta property="og:title" content="CIMM Computers - Ремонт техніки в Одесі" />
        <meta property="og:description" content="Професійний ремонт ноутбуків та телефонів. Чистка, прискорення, збірка ПК, обслуговування консолей. Безкоштовна діагностика. Гарантія якості." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cimm.od.ua" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_gadget-fix-center/artifacts/50glehzf_IMG_0941.jpeg" />
        <meta name="geo.region" content="UA-65" />
        <meta name="geo.placename" content="Одеса" />
        <meta name="geo.position" content="46.482526;30.712481" />
        <link rel="canonical" href="https://cimm.od.ua" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          '@id': 'https://cimm.od.ua/#business',
          name: 'CIMM Computers',
          image: 'https://cimm.od.ua/images/generated/console-hero.png',
          url: 'https://cimm.od.ua',
          telephone: '+380974178788',
          email: 'info@cimm.od.ua',
          priceRange: '₴₴',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Одеса',
            addressRegion: 'Одеська область',
            addressCountry: 'UA'
          },
          geo: { '@type': 'GeoCoordinates', latitude: 46.482526, longitude: 30.712481 },
          openingHoursSpecification: [{
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'],
            opens: '11:00',
            closes: '18:00'
          }],
          areaServed: [{ '@type': 'City', name: 'Одеса' }],
          founder: { '@type': 'Person', name: 'Войчук Дмитро Володимирович' },
          sameAs: ['https://www.instagram.com/cimmodua/', 'https://t.me/cimm_od_ua']
        })}</script>
      </Helmet>
      {/* Header */}
      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#services" className="text-gray-300 hover:text-orange-500 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(255,140,0,0.5)]">Послуги</a>
            <Link to="/phones" className="text-gray-300 hover:text-orange-500 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(255,140,0,0.5)]">Телефони</Link>
            <Link to="/remont-iphone" className="text-gray-300 hover:text-orange-500 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(255,140,0,0.5)]">Ремонт iPhone</Link>
            <Link to="/laptops" className="text-gray-300 hover:text-orange-500 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(255,140,0,0.5)]">Ноутбуки</Link>
            <Link to="/chistka-macbook" className="text-gray-300 hover:text-orange-500 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(255,140,0,0.5)]">MacBook</Link>
            <Link to="/chistka-noutbukiv" className="text-gray-300 hover:text-orange-500 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(255,140,0,0.5)]">Чистка ноутбуків</Link>
            <Link to="/chistka-pk" className="text-gray-300 hover:text-orange-500 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(255,140,0,0.5)]">Чистка ПК</Link>
            <Link to="/chistka-konsoley" className="text-gray-300 hover:text-orange-500 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(255,140,0,0.5)]">Чистка консолей</Link>
            <a href="#gallery" className="text-gray-300 hover:text-orange-500 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(255,140,0,0.5)]">Галерея</a>
            <a href="#testimonials" className="text-gray-300 hover:text-orange-500 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(255,140,0,0.5)]">Відгуки</a>
            <Link to="/contacts" className="text-gray-300 hover:text-orange-500 transition-all duration-300 hover:drop-shadow-[0_0_8px_rgba(255,140,0,0.5)]">Контакти</Link>
          </nav>
          <div className="flex items-center gap-3 md:gap-4">
            <ThemeToggle />
            <button onClick={() => setMobileOpen((v) => !v)} className="md:hidden text-gray-200 p-1.5" aria-label="Меню" data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div className="flex items-center gap-3">
              <a href="tel:+380974178788" className="neon-text font-semibold hover:drop-shadow-[0_0_15px_rgba(255,140,0,0.8)] transition-all duration-300" data-testid="header-phone-link">
                +38 097 417 8788
              </a>
              <HeaderSocials />
            </div>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-orange-500/20 bg-black/95 backdrop-blur px-4 py-3" data-testid="mobile-menu">
            <nav className="flex flex-col">
              {[
                ['/', 'Головна', 'home'],
                ['/remont-iphone', 'Ремонт iPhone', 'iphone'],
                ['/phones', 'Ремонт телефонів', 'phones'],
                ['/laptops', 'Ремонт ноутбуків', 'laptops'],
                ['/chistka-macbook', 'Чистка MacBook', 'macbook'],
                ['/chistka-noutbukiv', 'Чистка ноутбуків', 'laptop-cleaning'],
                ['/chistka-pk', 'Чистка ПК', 'pc'],
                ['/chistka-konsoley', 'Чистка консолей', 'consoles'],
                ['/contacts', 'Контакти', 'contacts']
              ].map(([to, label, key]) => (
                <Link key={key} to={to} onClick={() => setMobileOpen(false)} className="py-2.5 text-gray-200 hover:text-orange-500 border-b border-white/5 last:border-0 transition-colors" data-testid={`mobile-link-${key}`}>{label}</Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="hero-section pt-32 pb-20 px-4 grid-bg">
        <div className="container mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight">
                Професійний ремонт ноутбуків та телефонів в
                <span className="neon-text"> Одесі</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-300">
                Ноутбуки • Телефони • Планшети • Ігрові приставки
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 icon-glow" />
                  <span className="text-gray-300">Безкоштовна діагностика</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 icon-glow" />
                  <span className="text-gray-300">Гарантія якості</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 icon-glow" />
                  <span className="text-gray-300">Швидкий ремонт</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                <Button onClick={scrollToBooking} size="lg" className="futuristic-btn bg-orange-500 hover:bg-orange-600 text-white border-0 w-full sm:w-auto">
                  <Zap className="w-5 h-5 mr-2" />
                  Записатися на ремонт
                </Button>
                <Button variant="outline" size="lg" className="neon-border border-orange-500 text-orange-500 hover:bg-orange-500/10 bg-transparent w-full sm:w-auto">
                  <a href="tel:+380974178788">Зателефонувати</a>
                </Button>
              </div>
              <div className="flex items-center gap-4 pt-4 text-gray-300">
                <Clock className="w-5 h-5 text-orange-500 icon-glow" />
                <span className="font-medium">Пн-Сб: 11:00 - 18:00</span>
              </div>
            </div>
            <div className="relative">
              <HeroCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 dark-section">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Наші <span className="neon-text">послуги</span></h2>
            <p className="text-xl text-gray-400">Професійний ремонт та обслуговування вашої техніки</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockServices.map((service) => {
              const IconComponent = iconMap[service.icon];
              // Визначаємо куди веде кожна послуга
              const getServiceLink = (service) => {
                // Якщо є поле link в даних услуги, використовуємо його
                if (service.link) {
                  return service.link;
                }
                // Інакше використовуємо старий маппінг за id
                switch(service.id) {
                  case 1: return '/laptops';
                  case 2: return '/chistka-noutbukiv';
                  case 3: return '/phones';
                  case 4: return '/chistka-konsoley';
                  case 5: return '/chistka-konsoley';
                  case 6: return '#services';
                  case 7: return '#services';
                  case 8: return '#services';
                  default: return '#services';
                }
              };
              
              return (
                <Link 
                  to={getServiceLink(service)} 
                  key={service.id} 
                  className="glass-card p-6 group hover:scale-105 transition-all duration-300 cursor-pointer block"
                >
                  {service.image && (
                    <div className="w-full h-32 mb-4 rounded-lg overflow-hidden">
                      <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                    </div>
                  )}
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-8 h-8 text-orange-500 icon-glow" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                  <p className="text-gray-400 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                        <CheckCircle2 className="w-4 h-4 text-orange-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 flex items-center gap-2 text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-sm font-semibold">Дізнатися більше</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 grid-bg">
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Про <span className="neon-text">майстерню</span>
            </h2>
            <p className="text-xl text-gray-400">CIMM - Ваш надійний партнер у ремонті техніки</p>
          </div>
          
          <div className="glass-card p-8 space-y-6">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Чому обирають нас?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Досвідчені майстри</p>
                      <p className="text-gray-400 text-sm">Понад 10 років досвіду в ремонті електроніки</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Оригінальні запчастини</p>
                      <p className="text-gray-400 text-sm">Використовуємо тільки якісні та перевірені комплектуючі</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Швидкий ремонт</p>
                      <p className="text-gray-400 text-sm">Більшість ремонтів виконуємо за 1-2 години</p>
                    </div>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Наші переваги</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Безкоштовна діагностика</p>
                      <p className="text-gray-400 text-sm">Визначимо проблему абсолютно безкоштовно</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Гарантія на роботу</p>
                      <p className="text-gray-400 text-sm">Надаємо гарантію на всі виконані роботи</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Чесні ціни</p>
                      <p className="text-gray-400 text-sm">Прозора вартість без прихованих платежів</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="pt-6 border-t border-orange-500/20">
              <p className="text-gray-300 text-center">
                Ми працюємо в Одесі і надаємо послуги з ремонту ноутбуків, телефонів, планшетів та ігрових приставок. 
                Наша майстерня обладнана сучасним інструментом для якісного та швидкого ремонту вашої техніки.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Calculators Section */}
      <section className="py-20 px-4 dark-section">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              <span className="neon-text">Калькулятори</span> вартості
            </h2>
            <p className="text-xl text-gray-400">Розрахуйте орієнтовну вартість ремонту або чистки</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <Link to="/remont-iphone" className="glass-card p-6 group hover:scale-105 transition-transform duration-300" data-testid="iphone-repair-card">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500/20 to-cyan-600/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Smartphone className="w-8 h-8 text-teal-400 icon-glow" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Ремонт iPhone</h3>
              <p className="text-gray-400 mb-4">Заміна АКБ та дисплея, гарантія до 90 днів</p>
              <div className="flex items-center gap-2 text-orange-500">
                <span>Розрахувати</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            <Link to="/phones" className="glass-card p-6 group hover:scale-105 transition-transform duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Smartphone className="w-8 h-8 text-orange-500 icon-glow" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Ремонт телефонів</h3>
              <p className="text-gray-400 mb-4">Apple, Samsung, Xiaomi, Huawei</p>
              <div className="flex items-center gap-2 text-orange-500">
                <span>Розрахувати</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            <Link to="/laptops" className="glass-card p-6 group hover:scale-105 transition-transform duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Laptop className="w-8 h-8 text-orange-500 icon-glow" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Ремонт ноутбуків</h3>
              <p className="text-gray-400 mb-4">Apple, Dell, HP, Lenovo</p>
              <div className="flex items-center gap-2 text-orange-500">
                <span>Розрахувати</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            <Link to="/chistka-noutbukiv" className="glass-card p-6 group hover:scale-105 transition-transform duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <WashingMachine className="w-8 h-8 text-orange-500 icon-glow" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Чистка ноутбуків</h3>
              <p className="text-gray-400 mb-4">Звичайні та ігрові ноутбуки</p>
              <div className="flex items-center gap-2 text-orange-500">
                <span>Детальніше</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            <Link to="/chistka-konsoley" className="glass-card p-6 group hover:scale-105 transition-transform duration-300" data-testid="ps5-cleaning-card">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Gamepad2 className="w-8 h-8 text-orange-500 icon-glow" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Чистка PlayStation 5</h3>
              <p className="text-gray-400 mb-4">Заміна термопасти, усунення перегріву</p>
              <div className="flex items-center gap-2 text-orange-500">
                <span>Детальніше</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            <Link to="/chistka-macbook" className="glass-card p-6 group hover:scale-105 transition-transform duration-300" data-testid="macbook-card">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Laptop className="w-8 h-8 text-orange-500 icon-glow" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Обслуговування MacBook</h3>
              <p className="text-gray-400 mb-4">Ремонт, чистка та профілактика Apple</p>
              <div className="flex items-center gap-2 text-orange-500">
                <span>Детальніше</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            <Link to="/chistka-pk" className="glass-card p-6 group hover:scale-105 transition-transform duration-300" data-testid="pc-cleaning-card">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <HardDrive className="w-8 h-8 text-orange-500 icon-glow" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Чистка комп'ютерів</h3>
              <p className="text-gray-400 mb-4">Стаціонарні ПК: пил, термопаста, тиша</p>
              <div className="flex items-center gap-2 text-orange-500">
                <span>Детальніше</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            <Link to="/chistka-konsoley" className="glass-card p-6 group hover:scale-105 transition-transform duration-300" data-testid="consoles-cleaning-card">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Gamepad2 className="w-8 h-8 text-orange-500 icon-glow" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Чистка консолей</h3>
              <p className="text-gray-400 mb-4">PS5 / PS4 / Xbox — заміна термопасти</p>
              <div className="flex items-center gap-2 text-orange-500">
                <span>Детальніше</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Prices Section */}
      <section id="prices" className="py-20 px-4 grid-bg">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Прайс-<span className="neon-text">лист</span></h2>
            <p className="text-xl text-gray-400">Приблизна вартість послуг (точна ціна після діагностики)</p>
          </div>
          <div className="glass-card p-2">
            <div className="divide-y divide-orange-500/10">
              {mockPrices.map((item) => (
                <div key={item.id} className="price-item flex justify-between items-center p-4 transition-all duration-300">
                  <span className="text-gray-300">{item.service}</span>
                  <span className="font-semibold text-orange-500">{item.price}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-center text-gray-400 mt-6">
            * Діагностика завжди безкоштовна. Остаточна ціна залежить від складності ремонту.
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 px-4 dark-section">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Галерея <span className="neon-text">робіт</span></h2>
            <p className="text-xl text-gray-400">Приклади нашої роботи</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gallery.map((image) => {
              const GalleryItem = ({ children }) => {
                if (image.link) {
                  return (
                    <Link to={image.link} className="gallery-item group aspect-video bg-gray-900 cursor-pointer">
                      {children}
                    </Link>
                  );
                }
                return (
                  <div className="gallery-item group aspect-video bg-gray-900">
                    {children}
                  </div>
                );
              };

              return (
                <GalleryItem key={image.id}>
                  <img 
                    src={image.url} 
                    alt={image.title || image.alt || 'Gallery image'} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                    <p className="text-white font-medium p-4">
                      {image.title || image.alt || ''}
                      {image.link && <span className="ml-2 text-orange-500">→</span>}
                    </p>
                  </div>
                </GalleryItem>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 grid-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Відгуки <span className="neon-text">клієнтів</span></h2>
            <p className="text-xl text-gray-400">Що кажуть наші клієнти</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card p-6 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-white">{testimonial.name}</h3>
                  <div className="flex gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-orange-500 text-orange-500" />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-orange-500 mb-3">{testimonial.service}</p>
                <p className="text-gray-300 text-sm">{testimonial.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 dark-section">
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Часті <span className="neon-text">питання</span></h2>
            <p className="text-xl text-gray-400">Відповіді на найпоширеніші питання</p>
          </div>
          <div className="space-y-4">
            {mockFAQ.map((faq) => (
              <div key={faq.id} className="glass-card overflow-hidden">
                <details className="group">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <h3 className="text-lg font-bold text-white pr-4">{faq.question}</h3>
                    <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-open:rotate-180">
                      <ArrowRight className="w-5 h-5 text-orange-500 rotate-90" />
                    </div>
                  </summary>
                  <div className="px-6 pb-6 text-gray-300 border-t border-orange-500/20 pt-4">
                    {faq.answer}
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-20 px-4 dark-section">
        <div className="container mx-auto max-w-2xl relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Записатися на <span className="neon-text">ремонт</span></h2>
            <p className="text-xl text-gray-400">Залиште заявку і ми зв'яжемося з вами протягом 15 хвилин</p>
          </div>
          <div className="glass-card p-8 cyber-border">
            <form onSubmit={handleBookingSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Ваше ім'я</label>
                <Input 
                  required
                  value={bookingForm.name}
                  onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                  placeholder="Введіть ваше ім'я"
                  className="border-orange-500/30 text-white placeholder:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Телефон</label>
                <Input 
                  required
                  type="tel"
                  value={bookingForm.phone}
                  onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                  placeholder="+380 (XX) XXX-XX-XX"
                  className="border-orange-500/30 text-white placeholder:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Тип пристрою</label>
                <Input 
                  required
                  value={bookingForm.device}
                  onChange={(e) => setBookingForm({...bookingForm, device: e.target.value})}
                  placeholder="Наприклад: iPhone 12, MacBook Pro, PlayStation 5"
                  className="border-orange-500/30 text-white placeholder:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Опис проблеми</label>
                <Textarea 
                  required
                  value={bookingForm.issue}
                  onChange={(e) => setBookingForm({...bookingForm, issue: e.target.value})}
                  placeholder="Опишіть що сталося з пристроєм..."
                  rows={4}
                  className="border-orange-500/30 text-white placeholder:text-gray-500"
                />
              </div>
              <Button type="submit" className="w-full futuristic-btn bg-orange-500 hover:bg-orange-600 text-white border-0" size="lg">
                <Send className="w-4 h-4 mr-2" />
                Надіслати заявку
              </Button>
              <p className="text-xs text-white text-center" data-testid="booking-privacy-consent">
                Натискаючи кнопку, ви погоджуєтесь з{' '}
                <Link to="/privacy-policy" className="text-white font-semibold hover:text-gray-200 underline">Політикою конфіденційності</Link>.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 grid-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Контакти</h2>
            <p className="text-xl text-gray-400">Зв'яжіться з нами зручним способом</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div className="space-y-6">
              <div className="glass-card p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-orange-500 icon-glow" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Телефон</h3>
                    <a href="tel:+380974178788" className="text-orange-500 hover:text-orange-400 text-lg transition-colors">
                      +38 097 417 8788
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="glass-card p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-orange-500 icon-glow" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Години роботи</h3>
                    <p className="text-gray-400">Понеділок - Субота</p>
                    <p className="text-white font-medium">11:00 - 18:00</p>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-orange-500 icon-glow" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Адреса</h3>
                    <p className="text-gray-400">м. Одеса, вул. Одеська, 45</p>
                    <p className="text-sm text-orange-500 mt-1">cimm.od.ua</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-6 cyber-border">
              <h3 className="text-2xl font-bold text-white mb-2">Напишіть нам</h3>
              <p className="text-gray-400 mb-6">Ми відповімо якнайшвидше</p>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Ім'я</label>
                  <Input 
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    placeholder="Ваше ім'я"
                    className="border-orange-500/30 text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <Input 
                    required
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    placeholder="your@email.com"
                    className="border-orange-500/30 text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Повідомлення</label>
                  <Textarea 
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    placeholder="Ваше повідомлення..."
                    rows={4}
                    className="border-orange-500/30 text-white placeholder:text-gray-500"
                  />
                </div>
                <Button type="submit" className="w-full futuristic-btn bg-orange-500 hover:bg-orange-600 text-white border-0">
                  <Send className="w-4 h-4 mr-2" />
                  Надіслати
                </Button>
                <p className="text-xs text-gray-400 text-center" data-testid="contact-privacy-consent">
                  Натискаючи кнопку, ви погоджуєтесь з{' '}
                  <Link to="/privacy-policy" className="text-orange-500 hover:text-orange-400 underline">Політикою конфіденційності</Link>.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;